import {
  useState,
  useEffect,
  forwardRef,
  useRef,
  useImperativeHandle,
} from "react";
import { Radar } from "react-chartjs-2";
import "chart.js/auto";
import { useTranslation } from "react-i18next";
import { ChartOptions } from "chart.js/auto";
import { ActivityData, DeviceLoads, RadarData } from "../../../types/chartData";
import { DeviceType } from "../../../types/enums";
import { ChartJSOrUndefined } from "react-chartjs-2/dist/types";

type LoadsRadarChartProps = {
  deviceType: DeviceType;
  data1?: DeviceLoads | null;
  data2?: DeviceLoads | null;
  pc1: string;
  pc2?: string | null;
  height: number;
  selection: string;
  onRendered?: () => void;
};

export const LoadsRadarChart = forwardRef<
  ChartJSOrUndefined<"radar", number[], string> | undefined,
  LoadsRadarChartProps
>(
  (
    {
      deviceType,
      data1,
      data2,
      pc1,
      pc2,
      height,
      selection,
      onRendered,
    }: LoadsRadarChartProps,
    ref
  ) => {
   
    const { t } = useTranslation();

    const deviceLoads1 = data1;
    const deviceLoads2 =
      data2 === undefined || data2 === null ? null : data2;
    const [radarData, setRadarData] = useState<RadarData>(null);
    let projectCode1 = data1 === undefined ? t("unavailable") : pc1;

    let projectCode2 = data2 ? pc2 : t("unavailable");
    if (projectCode1 === projectCode2) {
      projectCode1 = projectCode1 + " (1)";
      projectCode2 = projectCode2 + " (2)";
    }
    function findMaxLoad(loads: (DeviceLoads | null | undefined)[]): number {
      let max = 0;

      for (const load of loads) {
        if (!load || load === undefined) continue;
        for (const key in load) {
          if (Object.prototype.hasOwnProperty.call(load, key)) {
            const value = load[key as keyof DeviceLoads];
            if (typeof value === "number" && value > max) {
              max = value;
            }
          }
        }
      }

      return max;
    }

    useEffect(() => {
      if (!deviceLoads1) {
        // Handle missing data appropriately
        setRadarData(null);
        return;
      }
      
    
      const data = buildLoadsData({
        deviceType: deviceType,
        deviceLoads: deviceLoads1,
        pc: projectCode1,
        r: 91,
        g: 191,
        b: 172,
      });
      if (deviceLoads2) {
        const data2 = buildLoadsData({
          deviceType: deviceType,
          deviceLoads: deviceLoads2,
          pc: projectCode2,
          r: 158,
          g: 158,
          b: 158,
        });
        if (data && data2) {
          data?.datasets.push(...data2.datasets);
        }
      }
     
      setRadarData(data);
    }, [deviceType, deviceLoads1, deviceLoads2, projectCode1, projectCode2]); // Dependencies

    if (!radarData) {
      return <p>No deviceLoads1 data available</p>;
    }

    //
    const options: ChartOptions<"radar"> = {
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: !data2
            ? ` ${selection} ${t("risk-factors")}`
            : `   ${t("risk-factor-comp")}`,
          align: "start",
          padding: {
            top: 5,
          },
        },
        legend: {
          display: projectCode2 === undefined ? false : true, // Hides the legend if the dashboardtype is normal
          align: "start",
        },
      },
      animation: {
        onComplete: () => {
          if (typeof onRendered === "function") {
            onRendered();
          }
        },
      },
      scales: {
        r: {
          ticks: {
            display: true,
            maxTicksLimit: 10,
            font: { size: 14 },
          },

          pointLabels: {
            //  font:{ size: 10 }, // Change this value to increase the font size of the point labels
          },
          suggestedMin: 0,
          suggestedMax: findMaxLoad([data1, data2]),
          // tooltips: {
          //   bodyFontSize: 24, // Change this value to increase the font size inside tooltips
          // },
        },
      },
    };

    return (
      <div
        style={{
          height: height,
          width: "99%",
        }}
      >
        <Radar ref={ref} data={radarData} options={options} />
      </div>
    );
  }
);

function buildLoadsData({
  deviceType,
  deviceLoads,
  pc,
  r,
  g,
  b,
}: {
  deviceType: DeviceType;
  deviceLoads: DeviceLoads;
  pc?: string | null;
  r: number;
  g: number;
  b: number;
}) {
  let data;
  if (deviceType === DeviceType.Back) {

    data = {
      labels: [
        "Peak Load Back",
        "Peak Load Core",
        "Median Load Back",
        "Median Load Core",
        "Static Load Back",
        "Static Load Core",
        "Fast Movements",
        "Bad posture",
        "Extreme Positions",
      ],
      datasets: [
        {
          label: pc,
          data: [
            deviceLoads.backPeakLoad,
            deviceLoads.backPeakLoadCore,
            deviceLoads.backMedianLoad,
            deviceLoads.backMedianLoadCore,
            deviceLoads.backStaticLoad,
            deviceLoads.backStaticLoadCore,
            deviceLoads.backFastMovements,
            deviceLoads.backBadPosture,
            deviceLoads.backExtremePositions,
          ],
          backgroundColor: `rgba(${r}, ${g}, ${b}, 0.2)`,
          borderColor: `rgba(${r}, ${g}, ${b}, 1)`,
          borderWidth: 1,
        },
      ],
    };

  } else if (deviceType === 0) {
    data = {
      labels: ["Peak Load", "Median Load", "Static Load", "Lack Of Rest"],
      datasets: [
        {
          label: pc,
          data: [
            deviceLoads.elbowPeakLoad,
            deviceLoads.elbowMedianLoad,
            deviceLoads.elbowStaticLoad,
            deviceLoads.elbowLackOfRest,
          ],
          backgroundColor: `rgba(${r}, ${g}, ${b}, 0.2)`,
          borderColor: `rgba(${r}, ${g}, ${b}, 1)`,
          borderWidth: 1,
        },
      ],
    };
  } else {
    return null;
  }

  return data;
}
